import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Accordion from 'react-bootstrap/Accordion'
import Select from 'react-select'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import AccordionHeaderContent from './AccordionHeaderContent'
import NotStartedStepMessage from './NotStartedStepMessage'
import { getStepState } from '../utils/getStepState'
import { PhoneInput, defaultCountries, parseCountry, buildCountryData } from 'react-international-phone'
import 'react-international-phone/style.css'
import Mask from '@helpers/Mask'
import { cpfIsValid } from '@helpers/Utils'
import IUser from '@interfaces/IUser'
import OrderStatusEnum from '@enums/OrderStatusEnum'

const thirdPartyDocumentTypeOptions = [
  { value: 'cpf', label: 'CPF' },
  { value: 'passport', label: 'Passaporte' },
]

interface DeclarationFormData {
  contract_holder: 'patient' | 'partner' | ''
  sample_destination: string
  third_party_name: string
  third_party_qualification: string
  third_party_cpf: string
  third_party_phone: string
  third_party_email: string
  contact_name: string
  contact_qualification: string
  contact_phone: string
  contact_email: string
  doctor_name: string
  doctor_crm: string
}

interface AccordionContractProps {
  stepProgress: number
  orderId: number | null
  user: IUser | null
  declarationForm: DeclarationFormData
  isSubmittingDeclaration: boolean
  isDeclarationSubmitted: boolean
  onFormChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFormSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  onPhoneChange: (field: 'third_party_phone' | 'contact_phone', value: string) => void
  isOrderInSubstatus: boolean
  orderStatus: OrderStatusEnum | null
}

const AccordionContract: React.FC<AccordionContractProps> = ({
  stepProgress,
  orderId,
  user,
  declarationForm,
  isSubmittingDeclaration,
  isDeclarationSubmitted,
  onFormChange,
  onFormSubmit,
  onPhoneChange,
  isOrderInSubstatus,
  orderStatus,
}) => {
  const stepState = getStepState(stepProgress)
  const canFillContract = orderStatus === OrderStatusEnum.SEND_CONTRACT
  const hasPartner = !!(user?.patient?.partner_name && user?.patient?.partner_document)
  const [thirdPartyDocumentType, setThirdPartyDocumentType] = useState<'cpf' | 'passport'>('cpf')
  const initializedOrderId = useRef<number | null>(null)

  useEffect(() => {
    if (orderId === null || initializedOrderId.current === orderId) return
    if (!declarationForm.third_party_cpf) return
    const digits = declarationForm.third_party_cpf.replace(/\D/g, '')
    setThirdPartyDocumentType(digits.length > 0 && digits.length !== 11 ? 'passport' : 'cpf')
    initializedOrderId.current = orderId
  }, [orderId, declarationForm.third_party_cpf])

  const handleThirdPartyDocumentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const Alert = withReactContent(Swal)
    let value = e.target.value

    if (thirdPartyDocumentType === 'cpf') {
      value = Mask.cpf(value)

      if (value.length > 14) {
        value = value.slice(0, 14)
      }

      if (value.length === 14 && !cpfIsValid(value)) {
        value = value.slice(0, 13)

        Alert.fire({
          title: 'Erro!',
          text: 'CPF inválido',
          icon: 'error',
          confirmButtonText: 'Ok',
        })
      }
    }

    const syntheticEvent = {
      target: { name: 'third_party_cpf', value },
    } as React.ChangeEvent<HTMLInputElement>

    onFormChange(syntheticEvent)
  }, [onFormChange, thirdPartyDocumentType])

  const countriesWithBrNewMask = useMemo(() => defaultCountries.map((country: any) => {
    const parsedCountry = parseCountry(country)

    if (parsedCountry.name === 'Brazil') {
      parsedCountry.format = '(..) .....-....'
    }

    return buildCountryData(parsedCountry)
  }), [])

  return (
    <Accordion.Item eventKey="5" className={`${stepState}-state`}>
      <Accordion.Header>
        <AccordionHeaderContent
          stepState={stepState}
          progress={stepProgress}
          title="Preenchimento do Contrato"
        />
      </Accordion.Header>

      <Accordion.Body>
        <div className="check-sample p-3">
          {stepProgress < 5 ? (
            <NotStartedStepMessage />
          ) : (
            <div className="select-payment">
              <div className="row">
                <div className="col-12 col-lg-12">
                  <div className="box box-declaration payment p-3">
                    <div className="title">
                      <div className="text">
                        <h5>Declaração</h5>
                        <p>Para geração do contrato de fornecimento, favor preencher os campos das declarações abaixo:</p>
                      </div>
                    </div>

                    <form className="declaration-form" onSubmit={onFormSubmit}>
                      <div className="row">
                        {/* Doctor */}
                        <div className="col-12 col-md-6">
                          <div className="form-group">
                            <label>Nome do médico <span className="text-danger ml-1">*</span></label>
                            <input
                              type="text"
                              name="doctor_name"
                              className="form-control"
                              value={declarationForm.doctor_name}
                              onChange={onFormChange}
                              required
                              readOnly={isDeclarationSubmitted || !canFillContract}
                            />
                          </div>
                        </div>

                        <div className="col-12 col-md-6">
                          <div className="form-group">
                            <label>CRM do médico <span className="text-danger ml-1">*</span></label>
                            <input
                              type="text"
                              name="doctor_crm"
                              className="form-control"
                              value={declarationForm.doctor_crm}
                              onChange={onFormChange}
                              required
                              readOnly={isDeclarationSubmitted || !canFillContract}
                            />
                          </div>
                        </div>

                        {/* Contract Holder */}
                        {hasPartner && (
                          <div className="col-12 mb-2">
                            <div className="form-group">
                              <label className="mb-0">Titular do Contrato:</label>
                              <div>
                                <small className="text-secondary">
                                  O contrato deverá ser realizado em <b>nome de quem irá gestar</b>:
                                </small>
                              </div>
                              <div className="checkbox-group mt-3">
                                <label className="checkbox d-flex align-items-center">
                                  <input
                                    className="input-select checkbox-round mr-3"
                                    type="radio"
                                    name="contract_holder"
                                    value="patient"
                                    checked={declarationForm.contract_holder === 'patient'}
                                    onChange={onFormChange}
                                    required
                                    disabled={isDeclarationSubmitted || !canFillContract}
                                  />
                                  <span>Paciente: {user?.name || '-'} ({user?.document || '-'})</span>
                                </label>
                                <label className="checkbox d-flex align-items-center">
                                  <input
                                    className="input-select checkbox-round mr-3"
                                    type="radio"
                                    name="contract_holder"
                                    value="partner"
                                    checked={declarationForm.contract_holder === 'partner'}
                                    onChange={onFormChange}
                                    required
                                    disabled={isDeclarationSubmitted || !canFillContract}
                                  />
                                  <span>Companheiro(a): {user?.patient?.partner_name || '-'} ({user?.patient?.partner_document || '-'})</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Death Declaration */}
                        <div className="col-12">
                          <div className="form-group">
                            <label className="mb-0">Declaração de Falecimento:</label>
                            <div>
                              <small className="text-secondary">
                              Em caso do meu falecimento, e se eu não tiver utilizado o sêmen, será dado o seguinte destino a amostra de sêmen adquirida e devidamente quitada:
                              </small>
                            </div>
                            <div className="checkbox-group mt-3">
                              <label className="checkbox d-flex align-items-center">
                                <input
                                  className="input-select checkbox-round mr-3"
                                  type="radio"
                                  name="sample_destination"
                                  value="desprezo"
                                  checked={declarationForm.sample_destination === 'desprezo'}
                                  onChange={onFormChange}
                                  required
                                  disabled={isDeclarationSubmitted || !canFillContract}
                                />
                                <span>Desprezo da Amostra de Sêmen</span>
                              </label>
                              <label className="checkbox d-flex align-items-center">
                                <input
                                  className="input-select checkbox-round mr-3"
                                  type="radio"
                                  name="sample_destination"
                                  value="clinica"
                                  checked={declarationForm.sample_destination === 'clinica'}
                                  onChange={onFormChange}
                                  required
                                  disabled={isDeclarationSubmitted || !canFillContract}
                                />
                                <span>Doação da Amostra de Sêmen para a Clínica/Médico Responsável</span>
                              </label>
                              <label className="checkbox d-flex align-items-center">
                                <input
                                  className="input-select checkbox-round mr-3"
                                  type="radio"
                                  name="sample_destination"
                                  value="conjuge"
                                  checked={declarationForm.sample_destination === 'conjuge'}
                                  onChange={onFormChange}
                                  required
                                  disabled={isDeclarationSubmitted || !canFillContract}
                                />
                                <span>Doação da Amostra de Sêmen para o Cônjuge mencionado em contrato ou terceiro abaixo indicado</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Third Party Section (conditional) */}
                        <div className={`col-12 third-party-section ${declarationForm.sample_destination === 'conjuge' ? '' : 'd-none'}`}>
                          <div className="row">
                            <div className="col-12 col-md-6">
                              <div className="form-group">
                                <label>Nome completo do terceiro para doação da amostra de sêmen</label>
                                <input
                                  type="text"
                                  name="third_party_name"
                                  className="form-control"
                                  value={declarationForm.third_party_name}
                                  onChange={onFormChange}
                                  readOnly={isDeclarationSubmitted || !canFillContract}
                                />
                              </div>
                            </div>

                            <div className="col-12 col-md-6">
                              <div className="form-group">
                                <label>Grau de parentesco do terceiro para doação da amostra de sêmen</label>
                                <input
                                  type="text"
                                  name="third_party_qualification"
                                  className="form-control"
                                  value={declarationForm.third_party_qualification}
                                  onChange={onFormChange}
                                  readOnly={isDeclarationSubmitted || !canFillContract}
                                />
                              </div>
                            </div>

                            <div className="col-12 col-md-6">
                              <div className="form-group">
                                <label>Tipo de documento do terceiro</label>
                                <Select
                                  className="react-select"
                                  placeholder=""
                                  isSearchable={false}
                                  isClearable={false}
                                  options={thirdPartyDocumentTypeOptions}
                                  value={thirdPartyDocumentType === 'passport' ? { value: 'passport', label: 'Passaporte' } : { value: 'cpf', label: 'CPF' }}
                                  onChange={(option: any) => setThirdPartyDocumentType(option?.value ?? 'cpf')}
                                  isDisabled={isDeclarationSubmitted || !canFillContract}
                                />
                              </div>
                            </div>
                            <div className="col-12 col-md-6">
                              <div className="form-group">
                                <label>{thirdPartyDocumentType === 'passport' ? 'Passaporte do terceiro para doação da amostra de sêmen' : 'CPF do terceiro para doação da amostra de sêmen'}</label>
                                <input
                                  type="text"
                                  name="third_party_cpf"
                                  className="form-control"
                                  value={declarationForm.third_party_cpf}
                                  onChange={handleThirdPartyDocumentChange}
                                  readOnly={isDeclarationSubmitted || !canFillContract}
                                />
                              </div>
                            </div>

                            <div className="col-12 col-md-6">
                              <div className="form-group">
                                <label>Telefone de contato do terceiro para doação da amostra de sêmen</label>
                                <PhoneInput
                                  defaultCountry="br"
                                  countries={countriesWithBrNewMask}
                                  inputClassName="w-100"
                                  name="third_party_phone"
                                  value={declarationForm.third_party_phone}
                                  onChange={(value) => onPhoneChange('third_party_phone', value)}
                                  disabled={isDeclarationSubmitted || !canFillContract}
                                />
                              </div>
                            </div>

                            <div className="col-12 col-md-6">
                              <div className="form-group">
                                <label>E-mail do terceiro para doação da amostra de sêmen</label>
                                <input
                                  type="email"
                                  name="third_party_email"
                                  className="form-control"
                                  value={declarationForm.third_party_email}
                                  onChange={onFormChange}
                                  readOnly={isDeclarationSubmitted || !canFillContract}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Contact Section */}
                        <div className="col-12 mt-2">
                          <p className="mb-0">Caso opte por contratar o serviço de armazenamento prolongado da amostra de sêmen adquirida:</p>
                          <small className="text-secondary">
                          Indique abaixo a pessoa que poderá ser contatada em caso de necessidade e não localização da própria contratante.
                          </small>
                        </div>

                        <div className="col-12 mt-3">
                          <div className="row">
                            <div className="col-12 col-md-6">
                              <div className="form-group">
                                <label>Nome completo do contato</label>
                                <input
                                  type="text"
                                  name="contact_name"
                                  className="form-control"
                                  value={declarationForm.contact_name}
                                  onChange={onFormChange}
                                  readOnly={isDeclarationSubmitted || !canFillContract}
                                />
                              </div>
                            </div>

                            <div className="col-12 col-md-6">
                              <div className="form-group">
                                <label>Grau de parentesco do contato</label>
                                <input
                                  type="text"
                                  name="contact_qualification"
                                  className="form-control"
                                  value={declarationForm.contact_qualification}
                                  onChange={onFormChange}
                                  readOnly={isDeclarationSubmitted || !canFillContract}
                                />
                              </div>
                            </div>

                            <div className="col-12 col-md-6">
                              <div className="form-group">
                                <label>Telefone do contato</label>
                                <PhoneInput
                                  defaultCountry="br"
                                  countries={countriesWithBrNewMask}
                                  inputClassName="w-100"
                                  name="contact_phone"
                                  value={declarationForm.contact_phone}
                                  onChange={(value) => onPhoneChange('contact_phone', value)}
                                  disabled={isDeclarationSubmitted || !canFillContract}
                                />
                              </div>
                            </div>

                            <div className="col-12 col-md-6">
                              <div className="form-group">
                                <label>E-mail do contato</label>
                                <input
                                  type="email"
                                  name="contact_email"
                                  className="form-control"
                                  value={declarationForm.contact_email}
                                  onChange={onFormChange}
                                  readOnly={isDeclarationSubmitted || !canFillContract}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Submit Button */}
                        {!isDeclarationSubmitted && canFillContract && (
                          <div className="col-12">
                            <div className="d-flex justify-content-end">
                              <button
                                type="submit"
                                className="save-btn btn btn-primary"
                                disabled={isSubmittingDeclaration}
                              >
                                {isSubmittingDeclaration ? 'Enviando...' : 'Enviar'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Accordion.Body>
    </Accordion.Item>
  )
}

export default AccordionContract
